import {
  EntryTypeEnum,
  IConfigOfferEntryDto,
  IRewardEntryDto,
  ISystemWideOfferEntryDto,
  ProductTypeDto,
} from 'generated/graphql-gateway';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';

import { MinimalCartEntryType } from './types';

export type LookUpContext = {
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>;
  indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto>;
  indexedRewards: IndexedMenu<IRewardEntryDto>;
};

/**
 * Determines the minimal cart entry type for a given entry ID by checking
 * various indexed collections in order of priority.
 *
 * Priority order:
 * 1. Donations
 * 2. Extras
 * 3. Rewards
 * 4. Offers (discount vs incentive)
 * 5. Menu entries (by type)
 */
export const getMinimalCartEntryType = (
  entryId: string,
  lookUpContext: LookUpContext
): MinimalCartEntryType => {
  const { indexedRewards, indexedOffers, indexedMenu } = lookUpContext;

  if (indexedRewards.find(entryId)) {
    return MinimalCartEntryType.reward;
  }

  const offerEntry = indexedOffers.find(entryId);
  if (offerEntry) {
    // Check if discountType is 'percentage' or 'amount' for discount, otherwise incentive
    return offerEntry.discountType === 'PERCENTAGE' || offerEntry.discountType === 'AMOUNT'
      ? MinimalCartEntryType.discount
      : MinimalCartEntryType.incentive;
  }

  const menuEntry = indexedMenu.find(entryId);
  return mapEntryTypeToMinimalCartEntryType(menuEntry?.type, menuEntry?.productType);
};

/**
 * Maps menu entry types to their corresponding minimal cart entry types.
 * Defaults to 'item' for unknown or missing types.
 */
const mapEntryTypeToMinimalCartEntryType = (
  entryType?: EntryTypeEnum,
  productType?: readonly ProductTypeDto[]
): MinimalCartEntryType => {
  switch (entryType) {
    case EntryTypeEnum.ITEM:
      if (productType?.includes(ProductTypeDto.NON_FOOD)) {
        // there are multiple items marked as non-food, at this point we are assuming
        // that anything we add to the cart that is non-food is either a donation or an extra
        return productType?.includes(ProductTypeDto.DONATIONS)
          ? MinimalCartEntryType.donation
          : MinimalCartEntryType.extra;
      }
      return MinimalCartEntryType.item;
    case EntryTypeEnum.COMBO:
      return MinimalCartEntryType.combo;
    case EntryTypeEnum.MODIFIER:
      return MinimalCartEntryType.modifier;
    default:
      return MinimalCartEntryType.item;
  }
};
